import * as Api from '@ViewModels';

/**
 * Base data structure for all edges (connections) in the React Flow diagram.
 *
 * Edges represent the flow of contacts from one step to another in the automation.
 * This base interface provides common properties shared by all edge types.
 *
 * The Record<string, unknown> extension allows edge-specific properties to be added
 * dynamically for specialized edge types like BranchEdgeData.
 *
 * @example
 * ```typescript
 * const simpleEdge: BaseEdgeData = {
 *   animated: true,
 *   label: 'Wait 1 day'
 * };
 * ```
 */
export interface BaseEdgeData extends Record<string, unknown> {
	/** Whether to show animated dots flowing along the edge */
	animated?: boolean;
	/** Optional text label displayed on the edge */
	label?: string;
	/** Whether this edge belongs to the currently highlighted validation path */
	isValidationPath?: boolean;
}

/**
 * Extended edge data for conditional branches from switch nodes.
 *
 * Branch edges connect switch nodes to different downstream paths based on contact
 * criteria. Each branch represents a case in the switch statement, with one branch
 * marked as the default fallback.
 *
 * Color system:
 * - Each case is assigned a unique color from BRANCH_COLORS constant
 * - Colors are used to visually distinguish different conditional paths
 * - The same color is applied to the edge, label, and any downstream nodes
 *   that inherit the parentCaseInfo
 *
 * Used by:
 * - BranchEdge component for rendering conditional connections
 * - Switch node editor for managing cases
 * - Contact flow visualization to show which path each contact takes
 *
 * @example
 * ```typescript
 * const vipBranchEdge: BranchEdgeData = {
 *   condition: {
 *     field: 'tags',
 *     operator: 'contains',
 *     value: 'VIP'
 *   },
 *   caseId: 'case-0',
 *   caseIndex: 0,
 *   isDefault: false,
 *   caseName: 'Has VIP Tag',
 *   caseColor: '#3b82f6',
 *   animated: true
 * };
 * ```
 */
export interface BranchEdgeData extends BaseEdgeData {
	/** Contact filter criteria that must be met to take this branch */
	condition?: Api.IContactFilterCriteria;
	/** Unique identifier for this case within the switch node */
	caseId?: string;
	/** Zero-based index of this case in the switch node's cases array */
	caseIndex?: number;
	/** Total number of cases in the parent switch node (used for label spacing) */
	totalCases?: number;
	/** Whether this is the default/fallback case when no other conditions match */
	isDefault?: boolean;
	/** Human-readable name describing the condition (e.g., "Has Tag: VIP") */
	caseName?: string;
	/** Hex color code for visually identifying this branch throughout the flow */
	caseColor?: string;
	/** ID of the hidden switch node that owns this branch (for data model tracking) */
	switchNodeId?: string;
}

/**
 * Constant mapping of all edge type identifiers.
 *
 * These constants define the available edge rendering styles in React Flow.
 * - DEFAULT: Basic straight edge
 * - BRANCH: Custom edge for conditional paths with color coding
 * - SMOOTHSTEP: Rounded corner edge (used for cleaner layouts)
 */
/**
 * Edge data for the phantom "Add Path" edge rendered to the right of the default segment.
 *
 * This edge displays as a dashed line with an inline "+" button, allowing users
 * to add new segments to the conditional flow. When clicked, creates a new blank
 * segment and shifts the default segment to the right.
 *
 * @example
 * ```typescript
 * const phantomEdge: PhantomAddPathEdgeData = {
 *   switchNodeId: 'step-123',
 *   segmentCount: 3
 * };
 * ```
 */
export interface PhantomAddPathEdgeData extends BaseEdgeData {
	/** ID of the parent switch node to add new segments to */
	switchNodeId: string;
	/** Current number of segments (used for positioning) */
	segmentCount: number;
}

export const EDGE_TYPES = {
	DEFAULT: 'default',
	BRANCH: 'branch',
	SMOOTHSTEP: 'smoothstep',
	PHANTOM_ADD_PATH: 'phantomAddPath',
} as const;
